import { and, eq } from "drizzle-orm";
import type { SQLiteColumn, SQLiteTable } from "drizzle-orm/sqlite-core";
import type { PowerSyncDrizzleDB } from "@listto/powersync";
import { nowISO } from "../utils/date-helpers";

/**
 * ── Local-first writes that CANNOT be lost silently ─────────────────────────
 *
 * A Drizzle `UPDATE … WHERE id = ?` against the PowerSync **view** can
 * match **ZERO rows and return "success"**. This is not a hypothesis: it was
 * measured in `space-academy-app` (same canon, local stack's `activity` table,
 * course `06a131d0`) — the course is created, 1.03 s later the unit is created, and
 * **between them there is NO `course/updated` op**: the title the author had just
 * of writing never generated a CRUD operation. In a run where
 * everything was queued before first upload (`136ef638`) it is present, 7 ms from
 * `created`.
 *
 * The mechanism: when the CRUD queue empties, PowerSync **rebuilds the
 * local view from the latest checkpoint**. If the checkpoint does not yet
 * contain the newly created row, the row is **ABSENT for that instant** — an
 * UPDATE against it writes nothing, generates no op and **does not fail**. This repo already
 * assumed that transient absence exists: it is the raison d'etre of
 * `apps/web/src/lib/use-entity-resolution.ts` ("row may not have
 * synchronized yet").
 *
 * Why the absence of an error proves nothing here: the server's write path
 * returns **2xx deliberately** (a 4xx would block the client's queue forever).
 * of client queue forever, see `infra/PENDING_WORK.md` §1.5.7 and
 * `lanzamiento-nativo-t0-t6/T6-auditoria-descartes.md`). That case is a DIFFERENT
 * DIFFERENT — there the server rejects and silences —, but criterion is the same:
 * a user write that disappears without trace is the worst failure mode.
 *
 * Contract of this module: write, **READ BACK**, and compare. If the value did
 * not land, retry with backoff; if it still has not landed when the retries
 * are exhausted, **THROW**—the caller reports it instead of leaving the user
 * looking at a field that represents nothing saved.
 *
 * ── What this module does NOT cover (deliberately) ──────────────────────────
 * Hard `DELETE`s (`purgeTodoMutation`, `purgeListMutation`,
 * `deleteListGroupOptimisticMutation`, `deleteSubtaskMutation`) are not verified:
 * its natural check would be "row is no longer there", and "absent" is the
 * ambiguous state in this window — cannot distinguish "deleted" from "not yet
 * had arrived". Verifying them would require another signal (CRUD queue itself), not
 * a reread. Signal that reopens: a purge genuinely lost
 * (row reappearing in trash after purge).
 *
 * ── `scope`: tenant isolation, not just correctness ──────────────────────────
 * Brought from `comandero-app` (`f8f1049`), where removing the
 * `organization_id` from `WHERE` when migrating to `patchRowVerified` **opened
 * cross-org writes**: `infra/sync_streams.yaml`
 * synchronizes ALL organizations the user is a `member` of (not
 * only active — YAML itself states "client scopes"), and `id` of the
 * row travels in the URL. Without extra filtering, an `UPDATE … WHERE id = ?` launched
 * from context of organization A landed equally on row of
 * organization B if `id` matched — tenant `WHERE` was not
 * redundant with the PK, it was isolation in depth. `scope` restores that
 * filter **applied to both UPDATE and reread**: if it were applied only
 * to one of the two, verification would lie (rereads without filter and confirms
 * a write that actually landed outside scope, or vice versa). A
 * row that does not satisfy scope is treated as absent row: the
 * write is never confirmed and function ends up throwing
 * `LocalWriteLostError` — same mechanism as checkpoint window, but
 * for right reason (not yours, not arrived late). `listto-app`
 * is not multi-org today, so no mutation here uses it
 * yet; lives here so multi-org clones (`field-check`,
 * `stockpilot`, `jornada`, `custodia`...) inherit it mechanically
 * instead of having to reinvent it under pressure.
 *
 * ── THREE pieces involving `updated_at`, and they are THREE different things ──
 * They overlap in the column name but not in behavior — each
 * has its own flag, and none implies the others:
 *
 *   1. **Stamping** (`hasUpdatedAt`, defaults to `true`): whether this helper
 *      writes `updated_at` in the `SET` of every attempt.
 *   2. **Ignore any `updated_at` supplied by the `patch`** (always, with no flag):
 *      it is never compared or passed through to SET unchanged.
 *   3. **Verify BY `updated_at`** (`verifyBy: "updated_at"`, opt-in): use
 *      the stamped timestamp as proof of landing instead of comparing the
 *      patch by value.
 *
 * ── 1. `hasUpdatedAt: false` — tables without that column ─────────────────
 * Brought from `vigia-fleet-app` (`6766c06`): `device_alert` and
 * `remote_control_session` do not have an `updated_at` column, and the canon
 * stamped unconditionally — would have crashed at runtime
 * against real SQLite (non-existent column), a failure that test doubles with
 * mock tables do NOT catch because they lack real schema behind them. With
 * `hasUpdatedAt: false`, `SET` does not touch that column at all, and
 * `verifyBy: "updated_at"` becomes invalid (throws on entry, before writing
 * anything, instead of failing against SQLite midway). **Prior check
 * for whoever converts a repo: do all tables you protect have
 * `updated_at`? If not, pass it explicitly — do not assume from rest of repo.**
 *
 * ── 2. Ignore `updated_at` coming in `patch` itself ──────────────────
 * Brought from `agape-church-app`: multiple mutations reuse same object
 * `values` between its INSERT and UPDATE branches, and that object already brings its
 * own `updated_at` for INSERT. If leaked without stripping, it would remain
 * compared as domain field against FRESH `updated_at` that this
 * helper stamps on each attempt — which never matches — and patch would fail
 * ALWAYS for wrong reason (not because write did not land, but because
 * because imported `updated_at` was outdated before writing).
 * writing). Ignored unconditionally, not only when it bites: not a
 * domain field, controlled by this module. Independent of `hasUpdatedAt`:
 * is ignored anyway even if table lacks column (a patch bringing it
 * there would be a caller error, not something that should leak to SET).
 *
 * ── 3. `verifyBy: "updated_at"` — EXPLICIT mode, never automatic ──────────
 * Also from `agape-church-app`: `bumpHeadcount` (attendance) uses atomic
 * atomic `sql\`MAX(0, COALESCE(headcount, 0) + ?)\`` in patch — read-add-
 * write in JS would reopen double-tap race condition that SQL avoids on
 * purpose. A SQL expression has no known expected value in
 * in advance: cannot compare by value upon reread. The first
 * draft of `agape` solved this with automatic *fallback* (if no literal
 * field remains in patch, compare `updated_at` instead); deliberately
 * discarded: silent fallback turns "cannot compare" into
 * "accept as good", masking cases where patch should NOT
 * being comparable by value (caller error, mistyped field). With
 * `verifyBy: "updated_at"` caller explicitly states "verify that
 * THIS write landed, not that value matches expected" — if omitted and
 * patch brings SQL expression, value comparison never matches and
 * function ends up throwing `LocalWriteLostError` even though the write has
 * landed: **fail-closed**, not a false green.
 *
 * ── Why there is NO `patchRowsVerified` / `waitForRowsPresent` (batch variants) ─
 * `folio-office-app` (`1a7b5b3`) and `apunte-digital-app` (`4cc1a014`) added
 * added to move a page and its cohort of subpages in one go
 * (`WHERE id IN (...)`, lands only when ALL rows in batch show
 * patch). `listto-app` has no mutation touching more than one row
 * per write — `purgeListMutation`/`restoreListMutation`/
 * `restoreListGroupItemMutation` operate row by row, without `inArray` in any
 * `packages/shared/src/**\/mutations.ts` (verified before deciding this, not
 * assumed) — so bringing batch primitive here would be dead code without
 * caller: kind of debt that bit in `lib/gestures`, which diverged in
 * 4 forms when copied to repos that did not need it. Signal that reopens
 * this: a mutation in `listto` moving more than one row with the
 * same timestamp (e.g. trash by cohort of lists+todos).
 *
 * ── Second class: an upsert that chooses INSERT-vs-UPDATE with its own SELECT ──
 *
 * `patchRowVerified`/`waitForRowPresent` protect the write against a row
 * whose `id` is ALREADY known. There is a different class that neither covers:
 * an upsert that decides whether to INSERT or UPDATE with its own SELECT because there is no
 * `id` to reread until that branch is chosen. That SELECT falls into the SAME
 * checkpoint window: if the row exists but has just reappeared after a
 * rebuild, the SELECT sees it as missing and the code INSERTs a new row—the data
 * is not lost, the row is DUPLICATED, and no error is visible.
 *
 * Characterized across five clones: `folio-office-app` (comment threads),
 * `habit-streak-app` (daily log), `boveda-vault-app`, `vigia-fleet-app`
 * (device onboarding — duplication **proven** by reverting and re-running) and
 * `meal-planner-app` (pantry aisles and weekly list). Solved in
 * `stockpilot-app` (`b8c4bc8a`) with `readMatchingRowVerified`, for its hard case:
 * `stock_quant` decides by CONTENT key (product×location×lot),
 * without an `id` the caller can know in advance.
 *
 * `readMatchingRowVerified` lives here in canon, but **`listto-app` does not
 * need it today**: no mutation in this repo decides INSERT-vs-UPDATE with a
 * dedicated SELECT (verified, not assumed — all `create*Mutation` in
 * `packages/shared/src/**\/mutations.ts` generate their `id` with `generateId()` and
 * INSERT directly; neither `onConflictDoUpdate` nor SELECT followed by
 * insert/update branch against PowerSync view). It is **hosted** so
 * that the five clones above (and any other adopting this form)
 * inherit it from mechanical conversion instead of reinventing under pressure —
 * just as happened with `scope` before `listto` was multi-org. **Do not
 * delete as "unused code"**: no caller in THIS repo on purpose,
 * has callers in five. Signal that reopens this: a `listto` mutation that
 * begins deciding INSERT-vs-UPDATE by content instead of by known `id`.
 *
 * Two ways to close this class, not interchangeable — full guide
 * (with criteria for choosing) is in
 * `infra/guides/POWERSYNC_VERIFIED_WRITE.md`:
 *
 *   1. **Explicit `isNew` from caller.** If caller of mutation already
 *      knows whether creating or updating (originates from "New" vs
 *      "Save" button, or `/new` route vs
 *      `/:id/edit`), pass as parameter and skip deciding SELECT
 *      completely. Simpler and zero-cost — closed this way in `folio-office-app` and
 *      `vigia-fleet-app`. Valid when caller knows; not valid
 *      when key is content-based and nobody knows in advance.
 *   2. **`readMatchingRowVerified`.** When row is identified by
 *      CONTENT (composite key, not an `id` brought by caller) and thus
 *      nobody knows in advance if it exists. Retries deciding read
 *      with same backoff as rest of module, BEFORE caller
 *      concludes "does not exist yet, must create".
 *
 * `readMatchingRowVerified` has real cost: does not distinguish "hidden" from
 * "genuinely new" without exhausting backoff, paying it in the most
 * common case (first time key is touched). If heavy in your domain —
 * as in `habit-streak-app`, the main interaction of that app —, a
 * third piece WRAPS path 2 without replacing it: see
 * `readMatchingRowVerifiedIfSuspect` / `markRecentContentInsert` below.
 */

/**
 * Backoff for retries. Sums to ≈ 6.05 s: amply covers the round trip of
 * a checkpoint (measured at ~1 s against local stack) without leaving a
 * fast interaction — mark important, complete — hanging forever if the
 * not exist.
 */
export const VERIFIED_WRITE_BACKOFF_MS = [50, 100, 200, 400, 800, 1500, 3000] as const;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/** Additional equality condition—deep tenant isolation. */
export interface ScopeCondition {
  column: SQLiteColumn;
  value: unknown;
}

export interface VerifiedWriteOptions {
  /** Wait times between retries. Exposed so tests do not take seconds. */
  backoffMs?: readonly number[];
  /**
   * Additional conditions the row must ALSO satisfy, besides `id`—
   * typically `organization_id`. They apply to both the UPDATE and the
   * reread: a row that does not satisfy the scope is treated like a
   * (see the security note above).
   */
  scope?: readonly ScopeCondition[];
  /**
   * Whether the table has an `updated_at` column. Defaults to `true` (the
   * usual behavior: it is stamped on every attempt). Set it to `false`
   * in tables that do NOT have that column (see note above, piece 1) — with
   * `false`, `patchRowVerified` never puts it in the `SET`, and `verifyBy:
   * "updated_at"` ceases to be valid (no column to compare).
   */
  hasUpdatedAt?: boolean;
  /**
   * How to verify that the write landed when the patch cannot be
   * compare by value (e.g. an atomic SQL increment expression).
   * EXPLICIT mode, never automatic — see note above, piece 3. `"updated_at"`
   * compares stamp that this helper stamps on each attempt: proves THIS
   * write landed, not that final value is expected. Requires
   * `hasUpdatedAt` other than `false`.
   */
  verifyBy?: "updated_at";
}

/** Error for a local-first write that did not land in the view. */
export class LocalWriteLostError extends Error {
  constructor(
    readonly rowId: string,
    readonly fields: readonly string[],
  ) {
    super(
      `La escritura local no aterrizó en la fila ${rowId} (campos: ${fields.join(", ")}). ` +
        `La fila no está en la vista de PowerSync o el valor no se guardó.`,
    );
    this.name = "LocalWriteLostError";
  }
}

/** Builds `eq(id) AND eq(scope1) AND …` — or just `eq(id)` with no scope. */
function idAndScopeCondition(idColumn: SQLiteColumn, id: string, scope: readonly ScopeCondition[]) {
  if (scope.length === 0) {
    return eq(idColumn, id);
  }
  return and(eq(idColumn, id), ...scope.map((s) => eq(s.column, s.value)));
}

/**
 * Reads the row by id (+ `scope`, if given), or `undefined` if it is not in the
 * local view or does not satisfy the scope—the same result as a "missing row",
 * deliberately (see the security note above).
 */
async function readRow(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  scope: readonly ScopeCondition[] = [],
): Promise<Record<string, unknown> | undefined> {
  // Drizzle cannot infer the shape of a generic `SQLiteTable`: the select
  // resolves to `{ [x: string]: any }[]`, so annotating the destination is enough—no
  // need to go through `unknown`; that would narrow without any real check.
  const rows: Record<string, unknown>[] = await db
    .select()
    .from(table)
    .where(idAndScopeCondition(idColumn, id, scope))
    .limit(1);
  return rows[0];
}

/** `null` and `undefined` are the same "empty" when compared against SQLite. */
const sameValue = (a: unknown, b: unknown): boolean => (a ?? null) === (b ?? null);

/**
 * `UPDATE … WHERE id = ?` **verified**: writes, reads back, compares the patch
 * fields, and retries while they have not landed. Throws `LocalWriteLostError`
 * if they are still missing when the backoff is exhausted.
 *
 * If `options.hasUpdatedAt` is not `false` (the normal case), this function sets
 * `updated_at` on every attempt—any `updated_at` supplied by the
 * `patch` itself is ALWAYS ignored, both when writing and when comparing (see
 * the note above, part 2); the rest of the `patch` must contain only domain
 * domain. With `hasUpdatedAt: false` (tables without column, see piece 1)
 * `SET` does not touch it at all. With `options.verifyBy === "updated_at"` it does not
 * compares patch by value: verifies that stamp of THIS attempt
 * landed (see note above, piece 3) — combining with `hasUpdatedAt: false`
 * is configuration error and throws immediately, before writing anything.
 */
export async function patchRowVerified(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  patch: Record<string, unknown>,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;
  const scope = options.scope ?? [];
  const hasUpdatedAt = options.hasUpdatedAt ?? true;
  if (options.verifyBy === "updated_at" && !hasUpdatedAt) {
    throw new Error(
      'verifyBy: "updated_at" is incompatible with hasUpdatedAt: false — the table does not have that column to compare.',
    );
  }
  const { updated_at: _ignoredUpdatedAt, ...domainPatch } = patch;

  for (let attempt = 0; ; attempt++) {
    const stampedAt = hasUpdatedAt ? nowISO() : undefined;
    await db
      .update(table)
      .set(hasUpdatedAt ? { ...domainPatch, updated_at: stampedAt } : domainPatch)
      .where(idAndScopeCondition(idColumn, id, scope));

    const row = await readRow(db, table, idColumn, id, scope);
    const landed =
      row !== undefined &&
      (options.verifyBy === "updated_at"
        ? sameValue(row.updated_at, stampedAt)
        : Object.entries(domainPatch).every(([key, value]) => sameValue(row[key], value)));
    if (landed) {
      return;
    }

    const wait = backoffMs[attempt];
    if (wait === undefined) {
      throw new LocalWriteLostError(id, Object.keys(domainPatch));
    }
    await sleep(wait);
  }
}

/**
 * Waits until row EXISTS in local view before touching it (+ `scope`,
 * if given, with the same tenant-isolation criterion as `patchRowVerified`).
 *
 * For mutations that are not a simple patch — a transaction completing
 * a recurring task and spawning the next, or an `order` swap
 * between two subtasks. Retrying inside transaction is invalid (would
 * sleep with write lock held), and damage of operating on missing row
 * is worse than losing a field: UPDATE matches nothing but companion
 * INSERT occurs, so original task never completes **and** phantom appears
 * its duplicated successor.
 */
export async function waitForRowPresent(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;
  const scope = options.scope ?? [];

  for (let attempt = 0; ; attempt++) {
    if (await readRow(db, table, idColumn, id, scope)) {
      return;
    }
    const wait = backoffMs[attempt];
    if (wait === undefined) {
      throw new LocalWriteLostError(id, ["<fila ausente>"]);
    }
    await sleep(wait);
  }
}

/**
 * Re-reads, with the same backoff as the rest of the module, a row that is
 * identified by CONTENT instead of `id` — the case of `stock_quant` in
 * `stockpilot-app` (product×location×lot key), or a comment thread/
 * daily log/device registration deciding INSERT-vs-UPDATE
 * inspecting content columns. See "Second class" note
 * above: `listto-app` has NO caller for this today — lives here
 * HOSTED for clones that do need it.
 *
 * It is called ALWAYS before opening the transaction that decides INSERT-vs-UPDATE, and
 * NEVER inside it—for the same reason as `waitForRowPresent`: retrying with
 * the write lock already held makes the whole transaction sleep, not just the
 * read. When multiple lines need resolving (order rows,
 * for example), resolve in PARALLEL (`Promise.all`) before
 * transaction: a genuinely new row then does not pay the latency of
 * another row's backoff that did require retries.
 *
 * Unlike `patchRowVerified`/`waitForRowPresent`, exhausting the backoff
 * is NOT an error here: genuine absence (the first time that content
 * combination is touched) is a VALID result, so this function
 * returns `undefined` instead of throwing, and caller proceeds to INSERT
 * exactly as if it had not retried. The backoff only reduces the
 * window of false negative; cannot eliminate entirely without another signal (
 * CRUD queue itself).
 *
 * `read` must repeat the SAME query on every attempt—a function that
 * rebuilds the `where` against `db`/`trx`, not an already-resolved promise
 * that would be reused without querying the database again.
 */
export async function readMatchingRowVerified<T>(
  read: () => Promise<T[]>,
  options: VerifiedWriteOptions = {},
): Promise<T | undefined> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;

  for (let attempt = 0; ; attempt++) {
    const [row] = await read();
    if (row) {
      return row;
    }
    const wait = backoffMs[attempt];
    if (wait === undefined) {
      return undefined;
    }
    await sleep(wait);
  }
}

/**
 * ── Real cost of `readMatchingRowVerified` — and its mitigation ──────────────
 *
 * `readMatchingRowVerified` cannot distinguish "hidden by checkpoint" from
 * "genuinely new" without exhausting backoff — paying it in BOTH cases. For
 * a content key (`habit_id`+`date`, `sku`+`warehouse_id`, ...) the case
 * "genuinely new" is usually the MOST common (first touch of day, first
 * movement of SKU), so exhausting backoff there is pure cost: ~6.05 s
 * by default to decide what a single read answers equally well
 * ("not found").
 *
 * Measured in `habit-streak-app` (`f531c275` → `f66f570b`): closing Class 2 of
 * `habit_completion` with plain `readMatchingRowVerified` was correct, but
 * caused **the app main interaction** — completing habit for
 * first time of day — to pay full backoff, since first touch of day
 * is precisely "genuinely new".
 *
 * The only time there IS reason to suspect window is when THIS
 * same process just INSERTed a row for that key — there, and only there,
 * the checkpoint may not have reached that local write yet.
 * `markRecentContentInsert` leaves that mark (in memory, per process — it is
 * lost on reload, exactly when no longer needed: view will already
 * have reconstructed from newer checkpoint); short TTL because
 * past margin, either it landed or backoff itself would not have
 * caught.
 *
 * `readMatchingRowVerifiedIfSuspect` WRAPS `readMatchingRowVerified` —
 * does not reimplement backoff or retry logic — only delegates to it
 * when live mark exists; without mark, single read decides. The bar of
 * "no duplication" that closed Class 2 is NOT relaxed: with mark,
 * behavior is exactly that of `readMatchingRowVerified` by itself.
 *
 * `listto-app` has NO Class 2 mutations (see "Second class" note
 * above: verified, not assumed), so — just like
 * `readMatchingRowVerified` itself — this pair lives HOSTED here, not because
 * canon uses it. Brought verbatim from `habit-streak-app` (`f66f570b`), where it
 * DOES have callers in production: `resolveCompletionRow`, shared by
 * three check-in mutations of `habit_completion`. Immediate candidate for a
 * second caller: `stockpilot-app@adjustStockMutation` decides by the same
 * content key class (product×location×lot) and currently accepts same
 * cost `habit-streak` had before this fix. **Do not delete as
 * "unused code"**: no caller in THIS repo on purpose, has caller in
 * at least one and candidate in another. Signal that reopens this: a mutation
 * in `listto` deciding INSERT-vs-UPDATE by content AND where first
 * touch is common case (not enough for Class 2 to exist — verified
 * that there are none, see above).
 */
const RECENT_CONTENT_INSERT_TTL_MS = 10_000;
const recentContentInserts = new Map<string, number>();

/** Marks that THIS process just INSERTed a row for `key` (content key). */
export function markRecentContentInsert(key: string): void {
  recentContentInserts.set(key, Date.now());
}

function hasRecentContentInsert(key: string): boolean {
  const insertedAt = recentContentInserts.get(key);
  if (insertedAt === undefined) {
    return false;
  }
  if (Date.now() - insertedAt > RECENT_CONTENT_INSERT_TTL_MS) {
    recentContentInserts.delete(key);
    return false;
  }
  return true;
}

/**
 * `readMatchingRowVerified`, but only pays backoff when there is a live mark
 * from `markRecentContentInsert` for `key`. Without mark: single read,
 * without retries — as valid as exhausting backoff to decide "not found"
 * when nothing suggests otherwise. With mark: full backoff, exactly
 * as calling `readMatchingRowVerified` directly (same function,
 * under the hood — see note above); if it finds the row, it clears the mark (no longer
 * window feared for next touch of same key).
 */
export async function readMatchingRowVerifiedIfSuspect<T>(
  key: string,
  read: () => Promise<T[]>,
  options: VerifiedWriteOptions = {},
): Promise<T | undefined> {
  if (!hasRecentContentInsert(key)) {
    const [row] = await read();
    return row;
  }
  const row = await readMatchingRowVerified(read, options);
  if (row) {
    recentContentInserts.delete(key);
  }
  return row;
}
